import { createSignal, onCleanup } from "solid-js";

export type Shortcut = {
  combo: string;
  description: string;
  scope: string;
  handler: (e: KeyboardEvent) => void;
};

const [registry, setRegistry] = createSignal<Shortcut[]>([]);

let chordBuffer = "";
let chordTimer: number | null = null;

function parseCombo(combo: string): string[] {
  return combo.split(/\s+/).filter(Boolean);
}

function eventCombo(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push("mod");
  if (e.shiftKey && e.key.length > 1) parts.push("shift");
  if (e.altKey) parts.push("alt");
  const k = e.key === " " ? "space" : e.key.toLowerCase();
  parts.push(k);
  return parts.join("+");
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

if (typeof window !== "undefined") {
  window.addEventListener("keydown", (e) => {
    const combo = eventCombo(e);
    const isMod = combo.startsWith("mod+");
    const typing = isTypingTarget(e.target);
    if (typing && !isMod && combo !== "escape") return;

    const buffered = chordBuffer ? `${chordBuffer} ${combo}` : combo;

    for (const s of registry()) {
      const seq = parseCombo(s.combo).join(" ");
      if (seq === buffered) {
        e.preventDefault();
        chordBuffer = "";
        if (chordTimer) { window.clearTimeout(chordTimer); chordTimer = null; }
        s.handler(e);
        return;
      }
      if (seq.startsWith(buffered + " ")) {
        chordBuffer = buffered;
        if (chordTimer) window.clearTimeout(chordTimer);
        chordTimer = window.setTimeout(() => { chordBuffer = ""; chordTimer = null; }, 900);
        e.preventDefault();
        return;
      }
    }
    chordBuffer = "";
    if (chordTimer) { window.clearTimeout(chordTimer); chordTimer = null; }
  });
}

export function useShortcut(combo: string, handler: (e: KeyboardEvent) => void, opts?: { description?: string; scope?: string }) {
  const entry: Shortcut = {
    combo,
    description: opts?.description ?? "",
    scope: opts?.scope ?? "global",
    handler,
  };
  setRegistry((r) => [...r, entry]);
  onCleanup(() => {
    setRegistry((r) => r.filter((s) => s !== entry));
  });
}

export function listShortcuts(): Shortcut[] {
  return registry();
}
